/**
 * OrganizationFormDialog
 * =====================================================================
 * Consolidates "Add New Organization" + "Edit Organization" dialogs
 * from App.js (lines ~62647 + ~62736).  Add had basic fields only;
 * Edit added a Practice Address block (Street / City / State /
 * ZIP+4 / Phone / Fax) with Medicare PECOS guidance.  We preserve
 * verbatim behavior by gating the address block to `editing=true`.
 *
 * Feb 2026 refactor.
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { DebouncedInput } from '../ui/debounced-textarea';
import { useZipAutofill } from '../../hooks/useZipAutofill';

const ORG_TYPES = [
  ['clinic', 'Clinic'],
  ['hospital', 'Hospital'],
  ['practice', 'Practice'],
  ['group', 'Group Practice'],
  ['facility', 'Healthcare Facility'],
];

export const OrganizationFormDialog = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  onSubmit,
  onCancel,
  editing = false,
}) => {
  const idp = editing ? 'edit-org' : 'org';
  const patch = (k, v) => setFormData({ ...formData, [k]: v });
  const zipDigits = (formData.zip_code || '').replace(/[^0-9]/g, '');

  // June 2026 — ZIP → city/state autofill (MA dataset).  Only fills
  // empty fields; user-entered city/state are never overwritten.
  // Hook strips +4 extensions before the 5-digit lookup, so this
  // still cooperates with the Medicare PECOS "ZIP+4 required" warning
  // below — biller can enter "01609" → autofill → then add "-2422".
  const zipAutofill = useZipAutofill({
    formData, setField: patch,
    zipField: 'zip_code', cityField: 'city', stateField: 'state',
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing ? 'Edit Organization' : 'Add New Organization'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {editing ? 'Update the practice organization details.' : 'Enter details for the new organization.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor={`${idp}-name`}>Organization Name *</Label>
              <DebouncedInput
                id={`${idp}-name`}
                value={formData.name}
                onChange={(e) => patch('name', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor={`${idp}-type`}>Organization Type *</Label>
              <Select
                value={formData.organization_type}
                onValueChange={(v) => patch('organization_type', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {ORG_TYPES.map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Address fields removed - organizations no longer have addresses
                here; Practice Address block (edit-only) lives below. */}
            <div>
              <Label htmlFor={`${idp}-tax-id`}>Tax ID</Label>
              <DebouncedInput
                id={`${idp}-tax-id`}
                value={formData.tax_id}
                onChange={(e) => patch('tax_id', e.target.value)}
                placeholder="12-3456789"
              />
            </div>
            <div>
              <Label htmlFor={`${idp}-taxonomy-code`}>Taxonomy Code</Label>
              <DebouncedInput
                id={`${idp}-taxonomy-code`}
                value={formData.taxonomy_code || ''}
                onChange={(e) => patch('taxonomy_code', e.target.value)}
                placeholder="e.g., 261QM0850X"
              />
              <p className="text-xs text-gray-600 mt-1">
                Facility taxonomy for claims submission
              </p>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor={`${idp}-npis`}>NPI Numbers (comma-separated)</Label>
              <DebouncedInput
                id={`${idp}-npis`}
                value={(formData.npis || []).join(', ')}
                onChange={(e) =>
                  patch(
                    'npis',
                    e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  )
                }
                placeholder="1234567890, 0987654321"
              />
            </div>
            {/* Jun 2026 — Availity/NEHEN interchange IDs (ISA06/ISA08)
                moved to the platform Superadmin portal (Tenants → EDI
                IDs) so tenants can't change them accidentally. The org
                settings page shows them read-only. */}
            {/* Practice Address — Edit-only, used for the 270
                Information Receiver Loop 2100B (N3/N4).  Medicare
                CEDI rejects 270s with AAA*50 'Invalid/Missing
                Provider ZIP Code' when the 5-digit ZIP doesn't match
                what's registered in PECOS for the org NPI.  Enter
                ZIP+4 (e.g. 01609-2422) to prevent that. */}
            {editing && (
              <>
                <div className="md:col-span-2 border-t pt-3 mt-2">
                  <p className="text-sm font-semibold text-slate-800 mb-1">
                    Practice Address
                  </p>
                  <p className="text-xs text-slate-500 mb-1">
                    Used for 270/837 submission. Medicare requires{' '}
                    <span className="font-mono">ZIP+4</span> matching what&apos;s
                    on file in PECOS.
                  </p>
                  <p className="text-xs text-amber-700 mb-3">
                    <strong>Match this address to PECOS exactly.</strong> Look
                    up your org NPI at{' '}
                    <a
                      className="underline"
                      href="https://npiregistry.cms.hhs.gov"
                      target="_blank"
                      rel="noreferrer"
                    >
                      npiregistry.cms.hhs.gov
                    </a>{' '}
                    and use the address shown there — including suite number
                    format (<span className="font-mono">STE 804</span>, not{' '}
                    <span className="font-mono">Suite 804</span>) — to avoid
                    Medicare AAA*50 rejections.
                  </p>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor={`${idp}-address`}>Street Address</Label>
                  <DebouncedInput
                    id={`${idp}-address`}
                    value={formData.address || ''}
                    onChange={(e) => patch('address', e.target.value)}
                    placeholder="100 Main St, Suite 412"
                    data-testid={`${idp}-address`}
                  />
                </div>
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor={`${idp}-city`}>City</Label>
                    <DebouncedInput
                      id={`${idp}-city`}
                      value={formData.city || ''}
                      onChange={(e) => patch('city', e.target.value)}
                      placeholder="Worcester"
                      data-testid={`${idp}-city`}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`${idp}-state`}>State</Label>
                    <DebouncedInput
                      id={`${idp}-state`}
                      value={formData.state || ''}
                      onChange={(e) =>
                        patch('state', e.target.value.toUpperCase().slice(0, 2))
                      }
                      placeholder="MA"
                      maxLength={2}
                      data-testid={`${idp}-state`}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`${idp}-zip`}>ZIP Code</Label>
                    <DebouncedInput
                      id={`${idp}-zip`}
                      value={formData.zip_code || ''}
                      onChange={(e) => zipAutofill.handleZipChange(e.target.value)}
                      placeholder="01609-2422"
                      data-testid={`${idp}-zip`}
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Use 9-digit ZIP+4 (e.g.{' '}
                      <span className="font-mono">01609-2422</span>) for
                      Medicare. Look up at{' '}
                      <a
                        className="underline"
                        href="https://npiregistry.cms.hhs.gov"
                        target="_blank"
                        rel="noreferrer"
                      >
                        npiregistry.cms.hhs.gov
                      </a>{' '}
                      using your org NPI.
                    </p>
                    {formData.zip_code && zipDigits.length < 9 && (
                      <p
                        className="text-xs text-amber-700 mt-1"
                        data-testid="org-zip-medicare-warning"
                      >
                        ⚠ This ZIP is only {zipDigits.length} digit
                        {zipDigits.length === 1 ? '' : 's'}. Medicare typically
                        rejects with AAA*50 unless a 9-digit ZIP+4 is provided.
                      </p>
                    )}
                    {zipAutofill.autofillStatus === 'filled' && (
                      <p className="text-xs text-emerald-600 mt-1" data-testid={`${idp}-zip-autofilled`}>
                        Auto-filled city/state from ZIP.
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <Label htmlFor={`${idp}-phone`}>Phone</Label>
                  <DebouncedInput
                    id={`${idp}-phone`}
                    value={formData.phone || ''}
                    onChange={(e) => patch('phone', e.target.value)}
                    placeholder="508-555-1234"
                    data-testid={`${idp}-phone`}
                  />
                </div>
                <div>
                  <Label htmlFor={`${idp}-fax`}>Fax</Label>
                  <DebouncedInput
                    id={`${idp}-fax`}
                    value={formData.fax || ''}
                    onChange={(e) => patch('fax', e.target.value)}
                    placeholder="508-555-5678"
                    data-testid={`${idp}-fax`}
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
              {editing ? 'Update Organization' : 'Create Organization'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OrganizationFormDialog;
